import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Handshake, Inbox, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useIncomingRepresentationRequests } from "@/hooks/platform/useAgency";

interface RequestMetadata {
  requester_name?: string;
  requester_id?: string;
}

/**
 * Incoming representation requests sent by creators/venues from their Agency tab.
 * Shown inside the Creator Management and Venue Oversight portals.
 */
const RepresentationRequests = () => {
  const navigate = useNavigate();
  const { userId } = useCurrentUser();
  const { data: requests, isLoading } = useIncomingRepresentationRequests(userId);


  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-white">
          <Handshake className="w-5 h-5" />
          Representation Requests
        </CardTitle>
        <Badge variant="secondary">{requests?.length ?? 0} total</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading requests…</p>}

        {!isLoading && (requests?.length ?? 0) === 0 && (
          <div className="py-10 text-center">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No representation requests yet. Creators and venues who request your agency from their
              Agency tab will appear here.
            </p>
          </div>
        )}

        {(requests ?? []).map((request) => {
          const metadata = (request.metadata ?? {}) as RequestMetadata;
          return (
            <div
              key={request.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-black/40 p-4"
            >
              <div className="min-w-0">
                <p className="font-medium text-white truncate">
                  {metadata.requester_name || request.title}
                </p>
                <p className="text-sm text-muted-foreground truncate">{request.body}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(request.created_at).toLocaleString()}
                  {!request.read_at && <span className="ml-2 text-primary">• New</span>}
                </p>
              </div>
              <Button size="sm" onClick={() => navigate("/messages")} className="shrink-0">
                <MessageSquare className="w-4 h-4 mr-2" />
                Reply
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default RepresentationRequests;
